import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { ThemeProvider } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';

import { denseTheme, desktopTheme } from '@/theme';

import { OperatingPhaseSwitch } from '@/components/pages/EmissionsDashboard/OperatingPhaseSwitch';

import { useGetBookingsExpandedQuery } from '@/graphql/GetBookingsExpanded.generated';

import { useBreakpoints } from '@/hooks';

import { ChatButton } from './AiAgent/ChatButton';
import { ChatWindow } from './AiAgent/ChatWindow';
import { AssetSearchButton } from './AssetSearchDialog';
import { DailyAverageChart } from './DailyAverageChart';
import { EnergyUsageChart } from './EnergyUsageChart';
import {
  ETSIndicator,
  ReeferCountIndicator,
  TotalCo2eIndicator,
} from './Indicators';
import { MonthRangePicker } from './MonthRangePicker';
import { ReeferAgesSelectFilter } from './ReeferAgesFilter';
import { ReeferKPI } from './ReeferKPI';
import { ReeferTypeBreakDownChart } from './ReeferTypeBreakDownChart';
import {
  RefrigerationUnitModelsFilterPanel,
  RefrigerationUnitModelsSelectFilter,
} from './RefrigerationUnitModelsFilter';
import { ShowHideResetControls } from './ShowHideResetControls';
import { TemperaturesSelectFilter } from './TemperaturesFilter';
import { TradelanesSelectFilter } from './TradelanesFilter';
import { TripList } from './TripList';
import { ViewModeSwitch } from './ViewModeSwitch';
import {
  getBookingsExpandedQueryWhereClauseFromFilters,
  setAssetIdsFromBookings,
  setConversationId,
  store,
} from './store';

const spacing = { xs: 1, sm: 1, md: 1, lg: 2, xl: 2 };

function EmissionsDashboard() {
  const breakpoints = useBreakpoints();
  const { t } = useTranslation();
  const { isMobileDevice, useUp, useDown } = breakpoints;
  const upSm = useUp('sm');
  const upLg = useUp('lg');
  const downMd = useDown('md');

  const [showFilters, setShowFilters] = useState(!isMobileDevice);
  const [chatOpen, setChatOpen] = useState(false);
  const toggleFilters = () => setShowFilters(!showFilters);

  const snap = useSnapshot(store);

  const [bookingsResult, _reexecuteGetBookingsQuery] =
    useGetBookingsExpandedQuery({
      pause: !snap.ready,
      variables: {
        where: getBookingsExpandedQueryWhereClauseFromFilters(
          snap as typeof store,
        ),
      },
    });

  useEffect(() => {
    setConversationId();
  }, []);

  useEffect(() => {
    if (bookingsResult.data) {
      setAssetIdsFromBookings(
        bookingsResult.data.container_insights_bookings_expanded,
      );
    }
  }, [bookingsResult.data]);

  return (
    <ThemeProvider theme={isMobileDevice ? denseTheme : desktopTheme}>
      <Stack direction="row" sx={{ width: '100%', pr: 0 }}>
        {/* Left panel with brands filter */}
        {!isMobileDevice && (
          <RefrigerationUnitModelsFilterPanel
            openedWidth={240}
            closedWidth={56 /* keep space for brands avatars */}
          />
        )}
        {/*
         * Main grid
         */}
        <Grid
          container
          size={{ xs: 12 }}
          sx={{
            pl: spacing,
            pt: spacing,
            pr: spacing,
            pb: 0,
          }}
        >
          <Grid container size={{ xs: 12 }} rowSpacing={spacing}>
            {/*
             * Header
             */}
            <Grid container size={{ xs: 12 }}>
              {/*
               * Dashboard title
               */}
              <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5 }}>
                <Stack direction={'row'} alignItems={'center'} spacing={2}>
                  {/* title */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ overflow: 'hidden' }}
                  >
                    <EnergySavingsLeafIcon
                      color="primary"
                      fontSize={upSm ? 'medium' : 'small'}
                    />

                    <Typography
                      variant={upLg ? 'h6' : 'subtitle1'}
                      fontWeight={upLg ? '' : 'bold'}
                      noWrap
                    >
                      {t('appDrawer.emissionsAndEnergy')}
                    </Typography>
                  </Stack>
                  {/* show/hide/reset filters buttons*/}
                  <ShowHideResetControls
                    open={showFilters}
                    onToggle={toggleFilters}
                  />
                </Stack>
              </Grid>

              {/* switch view mode (power or co2e) */}
              {!isMobileDevice && (
                <Grid size={{ xs: 12, sm: 12, md: 7, lg: 7 }}>
                  <Stack
                    spacing={1}
                    direction={'row'}
                    alignItems="center"
                    justifyContent={downMd ? 'flex-start' : 'flex-end'}
                  >
                    <AssetSearchButton />
                    <OperatingPhaseSwitch />
                    <ViewModeSwitch />
                  </Stack>
                </Grid>
              )}
            </Grid>
            {/*
             * Filters
             * Date range
             */}
            <Grid
              container
              spacing={1}
              sx={{
                display: showFilters ? 'flex' : 'none',
              }}
              size={{ xs: 12 }}
            >
              <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
                <MonthRangePicker />
              </Grid>
              {/*
               * Filters
               * Temperature & age
               */}
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <TemperaturesSelectFilter />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <ReeferAgesSelectFilter />
              </Grid>
              {/*
               * Filters
               * Tradelanes
               */}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 5 }}>
                <TradelanesSelectFilter />
              </Grid>
              {isMobileDevice && (
                <Grid
                  size={{ xs: 12 }}
                  sx={{ mb: 1 /* adjust margin for Indicators */ }}
                >
                  <RefrigerationUnitModelsSelectFilter />
                </Grid>
              )}
            </Grid>

            {/* switch view mode (power or co2e) */}
            {isMobileDevice && (
              <Grid size={{ xs: 12, sm: 12, md: 7, lg: 6 }}>
                <Stack
                  spacing={1}
                  direction={'row'}
                  alignItems="center"
                  justifyContent={downMd ? 'flex-start' : 'flex-end'}
                >
                  <AssetSearchButton />
                  <OperatingPhaseSwitch />
                  <ViewModeSwitch />
                </Stack>
              </Grid>
            )}

            {/*
             * Indicators (6 s)
             */}
            <Grid container size={{ xs: 12 }} columnSpacing={spacing}>
              {/*
               * First indicator
               */}
              <Grid size={{ xs: 4 }}>
                <ReeferCountIndicator />
              </Grid>
              {/*
               * Second indicator
               */}
              <Grid size={{ xs: 4 }}>
                <ETSIndicator />
              </Grid>
              {/*
               * Third indicator
               */}
              <Grid size={{ xs: 4 }}>
                <TotalCo2eIndicator />
              </Grid>
            </Grid>
            {/*
             * Main content grid (Charts & trip list)
             */}
            <Grid
              container
              size={{ xs: 12 }}
              columnSpacing={spacing}
              rowSpacing={spacing}
              alignItems={'flex-start'}
              sx={{ pb: 2 }}
            >
              {/*
               * Charts grid
               */}
              <Grid
                container
                size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 9 }}
                columnSpacing={spacing}
              >
                {/*
                 * Emissions charts
                 */}
                <Grid size={{ xs: 12 }}>
                  <EnergyUsageChart />
                </Grid>
                {/*
                 * Break down pie chart
                 */}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                  <ReeferTypeBreakDownChart />
                </Grid>
                {/*
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                  <DailyAverageChart />
                </Grid> */}
                {/*
                 * Reefer KPI
                 */}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                  <ReeferKPI />
                </Grid>
              </Grid>
              {/*
               * Trip list
               */}
              <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 3 }}>
                <TripList />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box sx={{ position: 'fixed', bottom: 0, right: 0, zIndex: 1000 }}>
          {!chatOpen && <ChatButton onClick={() => setChatOpen(true)} />}
          {chatOpen && <ChatWindow onClose={() => setChatOpen(false)} />}
        </Box>
      </Stack>
    </ThemeProvider>
  );
}

export { EmissionsDashboard };
